<script setup lang="ts">
import { useBookingResourcesStore } from '@/stores/bookingResources';
import { ref, computed, onMounted } from 'vue';
import { cloneDeep } from 'lodash';
import {
  isPast,
  addDays,
  endOfDay,
  startOfISOWeek,
  endOfISOWeek,
  isToday,
} from 'date-fns';
import { format } from '@/utils/date-fns';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import BookingSlot from '@/views/booking/BookingSlot.vue';
import { IProfile, IWeek } from '@/interfaces';
import WeekSelector from '@/views/booking/WeekSelector.vue';
import { eventBus } from '@/utils/eventBus';
import { useI18n } from 'vue-i18n';

const bookingResourcesStore: any = useBookingResourcesStore();

const participating = ref<boolean>(false);
const authFlowLoading = ref<boolean>(false);

interface Props {
  resource: any;
  moduleColor: string;
}

const props = withDefaults(defineProps<Props>(), {});
const showAuthFlow = ref<boolean>(false);
const selectedSlot = ref<any>(null);

const { t } = useI18n();

const currentWeek = {
  dateFrom: format(startOfISOWeek(new Date()), 'yyyy-MM-dd'),
  dateTo: format(endOfISOWeek(new Date()), 'yyyy-MM-dd'),
};

const selectedWeek = ref(currentWeek);

const selectedWeekAsDate = computed<IWeek>(() => {
  return {
    dateFrom: new Date(selectedWeek.value.dateFrom),
    dateTo: new Date(selectedWeek.value.dateTo),
  };
});

onMounted(async () => {
  await getBookingSlots();
});

const getBookingSlots = async () => {
  return bookingResourcesStore.getBookingSlots({
    id: props.resource.id,
    dateFrom: selectedWeek.value.dateFrom,
    dateTo: selectedWeek.value.dateTo,
  });
};

const slots = computed(() => bookingResourcesStore.selectedBookingSlots);

// Groups booking slots by id's, which will match if the 'time of day' is the same, creating an array for each timeslot.
// Each array contains slots that occur at the same time of day across different dates e.g: mon-sun 10:00-10:20.
const rows = computed(() => {
  const rows = cloneDeep(originalSlots.value).reduce((rv: any, x: any) => {
    (rv[x.id] = rv[x.id] || []).push(x);
    return rv;
  }, {});

  return rows;
});

const originalSlots = computed(
  () => bookingResourcesStore.selectedBookingSlots
);

const isPastByIndex = (index: number) => {
  const day = endOfDay(addDays(selectedWeek.value.dateFrom, index - 1));
  return isPast(day);
};

const getDate = (index: number) => {
  const date = addDays(selectedWeek.value.dateFrom, index - 1);
  const formatted = format(date, 'EEEE dd MMMM').split(' ');
  return {
    weekday: formatted[0],
    date: `${formatted[1]} ${formatted[2]}`,
  };
};

const getFullDate = (index: number) => {
  const date = addDays(selectedWeek.value.dateFrom, index - 1);
  return format(date, 'EEEE dd MMMM yyyy');
};

const formatTime = (dateTime: any) => {
  if (!dateTime) return '!date';
  return format(new Date(dateTime), 'HH:mm');
};

const onConfirmBooking = async (profile: IProfile, pin: string) => {
  try {
    authFlowLoading.value = true;
    const profileData: any = {
      profileId: profile.id,
      profileType: profile.type,
      pin,
    };

    const bookingSlot: any = {
      bookingResourceId: props.resource.id,
      bookingResourceSlotId: selectedSlot.value.id,
      date: format(new Date(selectedSlot.value.start), 'yyyy-MM-dd'),
    };

    if (selectedSlot.value.state === 'AVAILABLE') {
      await bookingResourcesStore.createNewSlottedBooking(
        profile.id,
        profile.type,
        bookingSlot
      );

      await getBookingSlots();
    } else {
      await bookingResourcesStore.cancelSlottedBooking(
        profileData,
        bookingSlot
      );

      await getBookingSlots();
    }

    eventBus.emit(
      'success',
      participating.value
        ? t('booking.bookingCreated')
        : t('booking.bookingDeleted')
    );
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showAuthFlow.value = false;
  }
};

const slotClicked = async (slot: any) => {
  if (['EXPIRED', 'LOCKED'].includes(slot.state)) return;
  participating.value = slot.state === 'AVAILABLE';
  showAuthFlow.value = true;
  selectedSlot.value = slot;
};

const onWeekChanged = (week: any) => {
  selectedWeek.value = week;
  getBookingSlots();
};
const onWeekReset = () => {
  selectedWeek.value = currentWeek;
  getBookingSlots();
};
</script>

<template>
  <div v-if="slots.length" class="absolute left-0 px-12 w-full mt-6">
    <div class="w-full grid grid-cols-8">
      <div v-for="(_, index) in 8" :key="index">
        <div></div>

        <div
          v-if="index > 0"
          class="text-center h200 rounded-t-md pt-6"
          :class="[
            isPastByIndex(index) ? 'text-neutral-700' : 'text-neutral-1000',
            isToday(getFullDate(index)) ? 'bg-darkNeutral600A' : '',
          ]"
        >
          <p
            v-if="isToday(getFullDate(index))"
            class="h300 h-[36px] bg-neutral-1000 text-neutral-0 w-[84px] rounded-full flex items-center justify-center mx-auto"
          >
            {{ $t('global.today') }}
          </p>
          <div class="h-[36px]" v-else>
            <p>{{ getDate(index).weekday }}</p>
            <p>{{ getDate(index).date }}</p>
          </div>
        </div>
      </div>
    </div>

    <div
      class="w-full pb-10 grid relative"
      :class="`grid-rows-${Object.keys(rows).length}`"
    >
      <div
        v-for="(day, i) in rows"
        :key="i"
        class="grid grid-cols-8 border-b border-neutral-300 last:border-b-0"
      >
        <div class="flex flex-col items-center justify-center h600">
          <p>{{ formatTime(day[0]?.start) }}</p>
          <p>{{ formatTime(day[0]?.end) }}</p>
        </div>

        <div
          v-for="(slot, index) in day"
          :key="index"
          class="flex items-center justify-center py-6 rounded-b-md"
          :class="[
            isToday(getFullDate((index as number) + 1))
              ? 'bg-darkNeutral600A'
              : '',
          ]"
        >
          <BookingSlot :slot="slot" @slot-clicked="slotClicked" />
        </div>
      </div>
    </div>

    <WeekSelector
      :selected-week="selectedWeekAsDate"
      @week-changed="onWeekChanged"
      @week-reset="onWeekReset"
    />
  </div>

  <p v-else class="w-full text-center text-error text-2xl mt-12">
    {{ $t('booking.noSchema') }}
  </p>

  <AuthFlow
    v-if="showAuthFlow"
    :title="
      participating ? $t('booking.bookResource') : $t('booking.cancelBooking')
    "
    :title-bold="props.resource.title"
    :loading="authFlowLoading"
    @close="showAuthFlow = false"
    @authenticated="onConfirmBooking"
  />
</template>
